import { theme } from '@zappyrent/hundop';
import { isEmpty } from 'lodash';
import { Info } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import Select, { GroupBase, SingleValue, StylesConfig } from 'react-select';

import {
  StyledHelperText,
  StyledHelperTextWrapper,
  StyledLabel,
  StyledWrapper,
} from './DropDownInput.styles';
import { IDropDownInputProps, IDropDownOption } from './DropDownInput.types';

const DropDownInput = ({
  id,
  name,
  label,
  control,
  rules,
  options,
  isLoading,
  disabled,
  placeholder,
  required = false,
  onSelect,
  value: initialValue,
  helperText,
}: IDropDownInputProps): JSX.Element => {
  const [customPlaceholder, setCustomPlaceholder] = useState(placeholder);
  useEffect(() => {
    const selectedOption = options?.find(
      option => option.value === initialValue,
    );
    if (selectedOption) {
      setCustomPlaceholder(selectedOption.label);
    }
  }, [initialValue, options]);
  const styles: StylesConfig<
    IDropDownOption,
    boolean,
    GroupBase<IDropDownOption>
  > = {
    control: base => ({
      ...base,
      fontSize: '16px',
      height: '40px',
      border: 'none',
      borderBottom: '2px solid #C8C8C8',
      boxShadow: 'none',
      borderWidth: '1px',
      borderRadius: 0,
      backgroundColor: 'transparent',
      ':disabled': {
        backgroundColor: '#EDF2F7',
      },
      ':hover': {
        borderColor: '#C8C8C8',
      },
    }),
    menuList: base => ({
      ...base,
      maxHeight: '200px',
    }),
    placeholder: base => ({
      ...base,
      fontSize: '16px',
      color: '#757575',
      padding: 0,
    }),
    option: base => ({ ...base, fontSize: '16px', zIndex: 9999 }),
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    clearIndicator: base => ({
      ...base,
      cursor: 'pointer',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    valueContainer: base => ({
      ...base,
      padding: '2px 0',
    }),
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: { onChange, onBlur, value, ref } }) => {
        const handleChange = (selectedOption: SingleValue<IDropDownOption>) => {
          onChange(selectedOption);
          if (onSelect && selectedOption) {
            const event = {
              target: {
                name: name,
                value: selectedOption.value,
              },
            } as React.ChangeEvent<HTMLSelectElement>;
            onSelect(event);
          }
        };
        return (
          <StyledWrapper
            isActive={true}
            isEmpty={isEmpty(value)}
            inputValue={value}
          >
            {label && (
              <StyledLabel htmlFor={id} required={required}>
                {label}
              </StyledLabel>
            )}
            <Select
              ref={ref}
              isClearable
              placeholder={customPlaceholder}
              options={options}
              value={value}
              onChange={handleChange}
              onBlur={onBlur}
              isDisabled={disabled}
              isLoading={isLoading}
              loadingMessage={() => label + ' is loading...'}
              isMulti={false}
              menuPosition="fixed"
              styles={styles}
              menuPortalTarget={document.body}
              onKeyDown={handleKeyDown}
            />
            {helperText && (
              <StyledHelperTextWrapper>
                <Info color={theme.colors.cross.grayson} size={15} />
                <StyledHelperText>{helperText}</StyledHelperText>
              </StyledHelperTextWrapper>
            )}
          </StyledWrapper>
        );
      }}
    />
  );
};

export default DropDownInput;
